import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Transaction } from "@shared/schema";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { formatCurrency, formatDate } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Loader2, Calendar, DollarSign, Tag, FileText, BookOpen, StickyNote, Store, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { VendorCombobox } from "./VendorCombobox";

// Extended transaction type — DB has columns not in the Drizzle schema type
type ExtendedTransaction = Transaction & {
  clientNotes?: string | null;
  journalEntryId?: number | null;
  journal_entry_id?: number | null;
  isSplit?: boolean;
  is_split?: boolean;
  coaAccountId?: number | null;
  coa_account_id?: number | null;
  vendorId?: number | null;
  vendor_id?: number | null;
  vendor_name?: string | null;
  splitEntries?: Array<{
    id: number;
    description: string;
    amount: string;
    categoryId: number | null;
    category_id?: number | null;
  }>;
};

interface TransactionDrawerProps {
  transaction: Transaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (id: number, data: any) => void;
}

function deriveVendor(description: string): string {
  if (!description) return "Unknown";
  return description
    .replace(/[*#]+/g, " ")
    .replace(/\d{3,}/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .slice(0, 3)
    .join(" ") || "Unknown";
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    approved: "bg-green-100 text-green-800",
    categorized: "bg-blue-100 text-blue-800",
    rule_applied: "bg-indigo-100 text-indigo-800",
    pending: "bg-yellow-100 text-yellow-800",
    for_review: "bg-orange-100 text-orange-800",
    excluded: "bg-gray-100 text-gray-500",
  };
  return map[status] || "bg-gray-100 text-gray-600";
}

export function TransactionDrawer({ transaction: rawTransaction, open, onOpenChange, onEdit }: TransactionDrawerProps) {
  const transaction = rawTransaction as ExtendedTransaction | null;
  const { activeBusiness } = useBusinesses();
  const businessId = activeBusiness?.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [clientNotes, setClientNotes] = useState(transaction?.clientNotes || "");
  const [selectedCoaId, setSelectedCoaId] = useState<string>("");
  const [notesChanged, setNotesChanged] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);

  // Resolve the effective COA id (handles both camelCase and snake_case from DB)
  const effectiveCoaId = transaction?.coaAccountId ?? transaction?.coa_account_id ?? null;

  // Sync state when transaction changes or vendor_id updates from server
  const txnVendorId = transaction?.vendorId ?? transaction?.vendor_id ?? null;
  useEffect(() => {
    setSelectedCoaId(effectiveCoaId?.toString() || "");
    setSelectedVendorId(txnVendorId);
    setClientNotes(transaction?.clientNotes || (transaction as any)?.client_notes || "");
    setNotesChanged(false);
  }, [transaction?.id, txnVendorId]);

  // Fetch COA accounts for category dropdown (expense + revenue types only)
  const { data: coaAccounts = [] } = useQuery({
    queryKey: ["/api/categories", businessId],
    queryFn: async () => {
      try {
        const r = await fetch(`/api/businesses/${businessId}/categories`, { credentials: "include" });
        if (!r.ok) return [];
        const data = await r.json();
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },
    enabled: !!businessId && open,
  });

  // Fetch full transaction details with split entries
  const isSplit = transaction?.isSplit || transaction?.is_split;
  const { data: fullTransaction } = useQuery({
    queryKey: ["/api/txn-detail", businessId, transaction?.id],
    queryFn: async () => {
      const r = await fetch(`/api/businesses/${businessId}/transactions/${transaction!.id}`, { credentials: "include" });
      if (!r.ok) return null;
      return r.json();
    },
    enabled: !!businessId && !!transaction?.id && !!isSplit && open,
  });
  const splitEntries: ExtendedTransaction["splitEntries"] = fullTransaction?.splitEntries || [];

  const jeId = transaction?.journalEntryId || transaction?.journal_entry_id;

  // Fetch linked journal entry
  const { data: journalEntry, isLoading: jeLoading } = useQuery({
    queryKey: ["/api/je-for-txn", businessId, transaction?.id],
    queryFn: async () => {
      if (!jeId) return null;
      const r = await apiRequest("GET", `/api/businesses/${businessId}/journal-entries/${jeId}`);
      return r.json();
    },
    enabled: !!businessId && !!jeId && open,
  });

  // Save notes mutation
  const saveNotes = useMutation({
    mutationFn: async () => {
      if (!transaction || !businessId) return;
      await apiRequest("PATCH", `/api/businesses/${businessId}/transactions/${transaction.id}`, {
        clientNotes,
      });
    },
    onSuccess: () => {
      toast({ title: "Notes saved" });
      setNotesChanged(false);
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to save notes", description: e.message, variant: "destructive" });
    },
  });

  // Categorize mutation
  const categorize = useMutation({
    mutationFn: async (coaAccountId: number) => {
      if (!transaction || !businessId) return;
      await apiRequest("PATCH", `/api/businesses/${businessId}/transactions/${transaction.id}/categorize`, {
        coaAccountId,
      });
    },
    onSuccess: () => {
      toast({ title: "Category updated" });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to update category", description: e.message, variant: "destructive" });
    },
  });

  // Save vendor selection
  const saveVendor = useMutation({
    mutationFn: async (vendorId: number | null) => {
      if (!transaction || !businessId) return;
      await apiRequest("PATCH", `/api/businesses/${businessId}/transactions/${transaction.id}`, {
        vendorId,
      });
    },
    onSuccess: () => {
      toast({ title: "Vendor updated" });
      // Invalidate all transaction queries so the table refreshes with the new vendor_name
      queryClient.invalidateQueries({
        predicate: (q) => {
          const key = q.queryKey[0]?.toString() || "";
          return key.includes(`/api/businesses/${businessId}/transactions`) || key.includes("/api/businesses");
        },
      });
    },
    onError: (e: any) => {
      toast({ title: "Failed to update vendor", description: e.message, variant: "destructive" });
    },
  });

  const handleVendorChange = (vendorId: number | null) => {
    setSelectedVendorId(vendorId);
    saveVendor.mutate(vendorId);
  };

  if (!transaction) return null;

  const vendor = transaction.vendor_name || transaction.merchantName || deriveVendor(transaction.description);
  const amount = parseFloat(transaction.amount);
  const isIncome = transaction.type === "income" || amount > 0;

  return (
    <Sheet open={open} onOpenChange={(o) => { setNotesChanged(false); onOpenChange(o); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-lg">Transaction Details</SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-2xl font-bold ${isIncome ? "text-green-600" : "text-red-600"}`}>
                {isIncome ? "+" : ""}{formatCurrency(Math.abs(amount))}
              </span>
              <Badge className={statusBadge(transaction.status || "pending")}>{transaction.status || "pending"}</Badge>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(transaction.date as any)}</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{transaction.description}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Vendor */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Store className="h-4 w-4" /> Vendor
              {saveVendor.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            </label>
            {businessId ? (
              <VendorCombobox
                businessId={businessId}
                value={selectedVendorId}
                onChange={handleVendorChange}
                placeholder={vendor}
              />
            ) : (
              <div className="text-sm bg-muted/50 rounded-md p-3">{vendor}</div>
            )}
          </div>

          <Separator />

          {/* Category */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Tag className="h-4 w-4" /> Category
            </label>
            <Select
              value={selectedCoaId || effectiveCoaId?.toString() || ""}
              onValueChange={(val) => {
                setSelectedCoaId(val);
                categorize.mutate(parseInt(val));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {(coaAccounts as any[]).map((a: any) => (
                  <SelectItem key={a.id} value={a.id.toString()}>
                    {a.code} — {a.name} ({a.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categorize.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          </div>

          <Separator />

          {/* Split Lines */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Split
              {isSplit && <Badge variant="secondary" className="text-xs">Split</Badge>}
            </label>
            <div className="border rounded-md p-3 space-y-2">
              {isSplit && splitEntries && splitEntries.length > 0 ? (
                splitEntries.map((entry, i) => {
                  const catId = entry.categoryId ?? entry.category_id;
                  const cat = catId ? (coaAccounts as any[]).find((a: any) => a.id === catId) : null;
                  return (
                    <div key={entry.id} className="flex justify-between items-start text-sm gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{entry.description || `Line ${i + 1}`}</span>
                        {cat && <span className="text-xs text-muted-foreground">{cat.code} — {cat.name}</span>}
                      </div>
                      <span className="font-mono whitespace-nowrap">
                        {formatCurrency(Math.abs(parseFloat(entry.amount)))}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">Not split (single category)</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Client Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <StickyNote className="h-4 w-4" /> Client Notes
            </label>
            <Textarea
              value={clientNotes}
              onChange={(e) => { setClientNotes(e.target.value); setNotesChanged(true); }}
              placeholder="Add notes about this transaction..."
              rows={3}
            />
            {notesChanged && (
              <Button
                size="sm"
                onClick={() => saveNotes.mutate()}
                disabled={saveNotes.isPending}
                className="gap-1"
              >
                {saveNotes.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save Notes
              </Button>
            )}
          </div>

          <Separator />

          {/* Journal Entry (read-only) */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Journal Entry
            </label>
            {!jeId ? (
              <p className="text-sm text-muted-foreground">No journal entry linked to this transaction.</p>
            ) : jeLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : journalEntry ? (
              <div className="border rounded-md p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">JE #{journalEntry.id}</span>
                  <Badge variant="outline">{journalEntry.status}</Badge>
                </div>
                {journalEntry.memo && (
                  <p className="text-muted-foreground text-xs">{journalEntry.memo}</p>
                )}
                {journalEntry.lines?.length > 0 && (
                  <table className="w-full text-xs mt-2">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Account</th>
                        <th className="text-right py-1">Debit</th>
                        <th className="text-right py-1">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journalEntry.lines.map((line: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1">{line.account_name || line.account_code || `Account #${line.account_id}`}</td>
                          <td className="text-right py-1 font-mono">{Number(line.debit) > 0 ? formatCurrency(Number(line.debit)) : ""}</td>
                          <td className="text-right py-1 font-mono">{Number(line.credit) > 0 ? formatCurrency(Number(line.credit)) : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Journal entry not found.</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
